import React, { useState, useEffect, ChangeEvent } from "react";
import { authService } from '../../../utils/authService';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  List,
//   Grid,
  ListItem,
  ListItemText,
  Snackbar,
  Modal,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Grid } from '@mui/material';
import BugReportIcon from "@mui/icons-material/BugReport";
import CommentIcon from "@mui/icons-material/Comment";
import { resolveMediaUrl } from "../../../utils/mediaUrl";
import CloseIcon from "@mui/icons-material/Close";
import { format } from 'date-fns';
interface CommentItem {
  id: string;
  comment: string;
  role: string;
  timestamp: Date;
}

interface Bug {
  sl_id: number;
  issue_description: string;
  studentname:string;
  image_path: string;
  issue_status: string;
  comments?: {
    [key: string]: {
      comment: string;
      role: string;
      timestamp: string;
    };
  };
}

interface DetailPanelProps {
  row: {
    original: Bug;
  };
  onCommentAdded?: () => void;
}

const DetailPanel: React.FC<DetailPanelProps> = ({ row, onCommentAdded }) => {
  const bug = row.original;
  const [comment, setComment] = useState<string>("");
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>("");
  const [imageModalOpen, setImageModalOpen] = useState<boolean>(false);
  const [comments, setComments] = useState<CommentItem[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [loading2, setLoading2] = useState<boolean>(false);

  const trainid = sessionStorage.getItem("Id")||"Unknown";

  useEffect(() => {
    if (bug.comments) {
      const sortedComments: CommentItem[] = Object.entries(bug.comments)
        .map(([key, value]) => ({
          id: key,
          comment: value.comment,
          role: value.role,
          timestamp: new Date(value.timestamp),
        }))
        .sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      setComments(sortedComments);
    }
  }, [bug.comments]);

  const handleCommentChange = (event: ChangeEvent<HTMLInputElement>) => {
    setComment(event.target.value);
  };

  const handleSendComment = async () => {
    if (!comment.trim()) {
      setSnackbarMessage("Comment cannot be empty");
      setSnackbarOpen(true);
      return;
    }
    setLoading(true);
    try {
      const response = await authService.put(
        "/admin/add_trainer_comment/",
        {
          bug_id: bug.sl_id,
          trainer_id: trainid,
          comment: comment,
        }
      );
      setSnackbarMessage(response.data.message || "Comment added successfully");
      setSnackbarOpen(true);
      setComment("");

      onCommentAdded?.();
    } catch (error) {
      console.error("Error sending comment:", error);
      setSnackbarMessage("Error sending comment");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleResolveBug = async () => {
    try {
      setLoading2(true);
      await authService.get(
        `/admin/resolve_ticket/${bug.sl_id}/`);
      setSnackbarMessage("Bug marked as resolved");
      setSnackbarOpen(true);
      onCommentAdded?.();
    } catch (error) {
      console.error("Error resolving bug:", error);
      setSnackbarMessage("Error resolving bug");
      setSnackbarOpen(true);
    } finally {
      setLoading2(false);
    }
  };

  return (
    <Box sx={{ p: 2 }} className="container-fluid">
      <Grid container  className="row" >
  <Grid className='col-lg-6 col-md-12 col-xl-6'>
    <Paper elevation={3} sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
      <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
        <BugReportIcon sx={{ mr: 1 }} />
        Issue Details
      </Typography>
      <Typography variant="body1" paragraph sx={{ fontSize: "0.9rem", mb: 2 }}>
        <strong>Description:</strong> {bug.issue_description}
      </Typography>
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={resolveMediaUrl(bug.image_path)}
          alt="Issue Preview"
          style={{
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "contain",
            cursor: "pointer",
          }}
          onClick={() => setImageModalOpen(true)}
        />
      </Box>
    </Paper>
  </Grid>

  <Grid className='col-lg-6 col-md-12 col-xl-6'>
    <Paper elevation={3} sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
      <Typography variant="h6" gutterBottom sx={{ display: "flex", alignItems: "center" }}>
        <CommentIcon sx={{ mr: 1 }} />
        Comments
      </Typography>
      <Box
        sx={{
          flexGrow: 1,
          overflowY: "auto",
          maxHeight: "200px",
          mb: 2,
        }}
      >
        <List>
          {comments.map((comment, index) => (
            <ListItem
              key={index}
              sx={{
                justifyContent: comment.role === "trainer" ? "flex-end" : "flex-start",
              }}
            >
              <Paper
                elevation={2}
                sx={{
                  p: 0.1,
                  maxWidth: "70%",
                  backgroundColor: comment.role === "trainer" ? "#e3f2fd" : "#f1f8e9",
                }}
              >
                <ListItemText
                  primary={comment.comment}
                  secondary={format(comment.timestamp, "MMM d, yyyy HH:mm")}
                  primaryTypographyProps={{ variant: "caption", color: "textPrimary" }}
                  secondaryTypographyProps={{ variant: "caption", color: "textSecondary" }}
                />
              </Paper>
            </ListItem>
          ))}
        </List>
      </Box>

      <TextField
        fullWidth
        multiline
        rows={2}
        variant="outlined"
        placeholder="Your comment"
        value={comment}
        onChange={handleCommentChange}
        sx={{ mb: 1 }}
      />

      <Button
        variant="contained"
        color="primary"
        onClick={handleSendComment}
        fullWidth
        disabled={loading}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : "Add Comment"}
      </Button>

      <Button
        variant="contained"
        color="primary"
        onClick={handleResolveBug}
        disabled={bug.issue_status === "Resolved" || loading2}
        fullWidth
        sx={{ mt: 2 }}
      >
        {loading2 ? (
          <CircularProgress size={24} color="inherit" />
        ) : bug.issue_status === "Resolved" ? (
          "Ticket Resolved"
        ) : (
          "Resolve Ticket"
        )}
      </Button>
    </Paper>
  </Grid>
</Grid>


      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />

      <Modal
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        aria-labelledby="image-modal"
        aria-describedby="full-size-issue-image"
      >
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            maxWidth: "90vw",
            maxHeight: "90vh",
            overflow: "auto",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <IconButton
            aria-label="close"
            onClick={() => setImageModalOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box sx={{ mt: 2, width: "100%", display: "flex", justifyContent: "center" }}>
            <img
              src={resolveMediaUrl(bug.image_path)}
              alt="Full Size Issue"
              style={{ width: "100%", height: "auto", objectFit: "contain" }}
            />
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default DetailPanel;
